import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Check, ChevronDown, ChevronUp, AlertTriangle,
  Users, UserX, Archive, TrendingUp, X,
} from 'lucide-react';
import { MetricCard } from './MetricCard';
import { useMetricsPanelUrl } from '@/hooks/useMetricsPanelUrl';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { PsychologistMetrics } from '../../../../hooks/api/psychologist/metrics';
import apiCoreAdapter from '../../../../hooks/apiCoreAdapter';

// TODO: reemplazar con flag real de la API cuando esté disponible
const MOCK_ETAPAS_COMPLETADAS: number[] = [];
const MOCK_EN_PAUSA = false;

interface Props {
  metrics: PsychologistMetrics | null;
  loading: boolean;
}

// ── Conteo de procesos por estado ────────────────────────────────────────────
// Misma fuente y mismo campo (process_patient.status) que usa la tab de Pacientes
// (ver usePatientsData.ts) — no el /metrics agregado, que puede quedar desincronizado
// del estado real para psicólogas en transición N→O.

type PatientStatusName = 'lead' | 'active' | 'on_hold' | 'inactive' | 'transferred';
const VISIBLE_PATIENT_STATUSES: readonly PatientStatusName[] = ['lead', 'active', 'on_hold', 'inactive', 'transferred'];

interface RawPatientStatusResult {
  patient_status?: string | null;
  session_total?: number | null;
  next_session_date?: string | null;
}

interface ProcessCounts {
  active: number;
  inactive: number;
  onHold: number;
  transferred: number;
  leads: number;
  historical: number; // active + inactive + on_hold + transferred
}

function useProcessCounts() {
  const [counts, setCounts] = useState<ProcessCounts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiCoreAdapter.patientsInformation({ page: 1, per_page: 800 })
      .then((resp) => {
        if (cancelled) return;
        const list = (Array.isArray(resp?.results) ? resp.results : []) as RawPatientStatusResult[];
        let active = 0, inactive = 0, onHold = 0, transferred = 0, leads = 0;
        for (const r of list) {
          const status = r.patient_status;
          if (!status || !(VISIBLE_PATIENT_STATUSES as readonly string[]).includes(status)) continue;
          if (status === 'lead') {
            // Excluye leads cancelados (0 sesiones y sin próxima sesión), igual que la tab de Pacientes.
            if ((r.session_total ?? 0) <= 0 && !r.next_session_date) continue;
            leads += 1;
          } else if (status === 'active') active += 1;
          else if (status === 'inactive') inactive += 1;
          else if (status === 'on_hold') onHold += 1;
          else if (status === 'transferred') transferred += 1;
        }
        setCounts({ active, inactive, onHold, transferred, leads, historical: active + inactive + onHold + transferred });
      })
      .catch(() => { if (!cancelled) setCounts(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return { counts, loading };
}

interface Etapa {
  numero: 1 | 2 | 3;
  titulo: string;
  descripcion: React.ReactNode;
  resultadoEsperado: string;
}

type PanelType = 'proceso' | 'adherencia' | 'horarios' | null;

// ── Panel lateral ────────────────────────────────────────────────────────────

const PROCESS_STATES = [
  {
    label: 'Nuevo',
    color: 'bg-[#ECFFD5] text-[#2D2049]',
    description: 'Próximo a iniciar: El proceso fue asignado a vos pero aún no tuvo su primera sesión.',
  },
  {
    label: 'Activo',
    color: 'bg-[#E9E0FC] text-[#4A3D73]',
    description: 'Tuvo sesión en los últimos 30 días.',
  },
  {
    label: 'Inactivo',
    color: 'bg-[#FFDEDE] text-[#2D2049]',
    description: 'Pasaron 30 días desde la última sesión.',
  },
];

interface InfoPanelProps {
  type: PanelType;
  onClose: () => void;
}

const PANEL_TITLES: Record<Exclude<PanelType, null>, string> = {
  proceso: '¿Qué es un proceso terapéutico?',
  adherencia: '¿Qué es la adherencia temprana?',
  horarios: 'Límite interno según etapa',
};

const InfoPanel: React.FC<InfoPanelProps> = ({ type, onClose }) => {
  const isOpen = type !== null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 bg-black/30 z-[9998] transition-opacity duration-200 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed right-0 top-0 h-full w-[440px] max-w-full bg-white shadow-2xl z-[9999] flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 gap-3">
          <span className="text-sm font-semibold text-[#2D2049] leading-snug">
            {type ? PANEL_TITLES[type] : ''}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            aria-label="Cerrar panel"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {type === 'proceso' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-[1.8]">
                Cada vez que empezás a atender a un paciente con un tipo de terapia, se abre
                un proceso terapéutico. Es la relación terapéutica entre vos y ese paciente —
                la combinación de psicóloga + paciente + tipo de terapia (individual, pareja,
                familia, etc).
              </p>
              <p className="text-sm text-gray-600 leading-[1.8]">
                Un proceso que pasa a inactivo puede ser reactivado: si el paciente retoma las
                sesiones, el proceso vuelve a estado activo automáticamente.
              </p>
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Estados de un proceso
                </p>
                <div className="flex flex-col divide-y divide-gray-100">
                  {PROCESS_STATES.map((s) => (
                    <div key={s.label} className="flex items-start gap-4 py-3.5 first:pt-0 last:pb-0">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 w-24 text-center ${s.color}`}>
                        {s.label}
                      </span>
                      <p className="text-sm text-gray-600 leading-relaxed">{s.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {type === 'horarios' && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600 leading-[1.8]">
                Esta es la cantidad de procesos que podemos asignarte según tu etapa inicial y tu performance actual. Este número se actualiza todos los meses durante tu período inicial, es importante que lo vayas revisando para asegurarte de tener disponibilidad, en caso de ser elegible para asignarte pacientes nuevos.
              </p>
              <p className="text-sm text-gray-600 leading-[1.8]">
                El límite varía según la etapa en la que estés — podés ver el detalle en la sección <strong>¿Cuál es el camino de crecimiento?</strong> de esta misma tab.
              </p>
              <p className="text-sm text-gray-600 leading-[1.8]">
                ¡Asegurate de tener horarios disponibles para esta cantidad de pacientes!
              </p>
            </div>
          )}

          {type === 'adherencia' && (
            <div className="flex flex-col gap-5">
              <p className="text-sm text-gray-600 leading-[1.8]">
                La adherencia temprana mide cuántos de los procesos que recibís logran
                sostenerse en las primeras semanas — específicamente, cuántos llegan a tener
                una segunda sesión dentro de los primeros 30 días.
              </p>
              <p className="text-sm text-gray-600 leading-[1.8]">
                Es la señal más clara que tenemos de que el vínculo terapéutico está
                funcionando. Un proceso que llega a una segunda sesión tiene muchas más
                chances de sostenerse en el tiempo.
              </p>
              <div className="bg-[#F3EFFF] rounded-xl px-4 py-4">
                <p className="text-sm text-[#4A3D73] leading-[1.8]">
                  El sistema evalúa tu adherencia temprana al cierre de cada etapa para
                  decidir si avanzás a la siguiente y si tu límite de procesos sube.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

// ── Concepto clickeable ──────────────────────────────────────────────────────

interface ConceptLinkProps {
  children: React.ReactNode;
  onClick: () => void;
  white?: boolean;
}

const ConceptLink: React.FC<ConceptLinkProps> = ({ children, onClick, white }) => (
  <button
    type="button"
    onClick={onClick}
    className={`underline decoration-dotted underline-offset-2 font-medium cursor-pointer transition-colors ${
      white
        ? 'text-white hover:opacity-80'
        : 'text-[#665090] hover:text-[#4A3D73]'
    }`}
  >
    {children}
  </button>
);

// ── Componente principal ─────────────────────────────────────────────────────

export const MetricsAsignacionNTab: React.FC<Props> = ({ metrics, loading }) => {
  const etapasCompletadas = MOCK_ETAPAS_COMPLETADAS;
  const enPausa = MOCK_EN_PAUSA;
  const { counts: processCounts, loading: countsLoading } = useProcessCounts();

  const [expandedEtapa, setExpandedEtapa] = useState<number | null>(null);
  const [equipoFijoExpanded, setEquipoFijoExpanded] = useState(false);
  const [expandedEquipoSection, setExpandedEquipoSection] = useState<string | null>(null);
  const [openPanel, setOpenPanel] = useState<PanelType>(null);
  useMetricsPanelUrl<Exclude<PanelType, null>>('asignacion-inicial', openPanel, setOpenPanel);

  const toggleEquipoSection = (key: string) =>
    setExpandedEquipoSection((prev) => (prev === key ? null : key));

  const toggleEtapa = (num: number) =>
    setExpandedEtapa((prev) => (prev === num ? null : num));

  const isCompletada = (num: number) => etapasCompletadas.includes(num);

  const proc = <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink>;
  const adherencia = <ConceptLink onClick={() => setOpenPanel('adherencia')}>adherencia temprana</ConceptLink>;

  const ETAPAS: Etapa[] = [
    {
      numero: 1,
      titulo: 'Tus primeros 5 procesos',
      descripcion: (
        <>
          Recibís tus primeros 5 {proc} de forma gradual. El objetivo de esta etapa es que la
          mayoría llegue a tener al menos una segunda sesión dentro de los primeros 30 días de
          su primera sesión.
        </>
      ),
      resultadoEsperado: 'Si lográs generar esa adherencia, podés avanzar a la Etapa 2 con 10 pacientes en total!',
    },
    {
      numero: 2,
      titulo: 'Tus primeros 10 procesos',
      descripcion: (
        <>
          Con 10 {proc}, repetimos la evaluación: cuántos llegaron a tener su segunda sesión en
          los primeros 30 días.
        </>
      ),
      resultadoEsperado: 'Si lográs generar buena adherencia, avanzás a la Etapa 3. Si lográs generar excelente adherencia, acá termina directamente tu período inicial de adaptación!',
    },
    {
      numero: 3,
      titulo: 'Tus primeros 15 procesos',
      descripcion: (
        <>
          Esta etapa aplica según cómo salió tu Etapa 2. Con 15 {proc}, evaluamos una vez más
          la {adherencia}.
        </>
      ),
      resultadoEsperado: 'Si lo lográs, acá termina tu período inicial de adaptación.',
    },
  ];

  return (
    <>
      <InfoPanel type={openPanel} onClose={() => setOpenPanel(null)} />

      <div className="flex flex-col gap-8">
        {/* ── A. Métricas ──────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-semibold text-[#4A3D73]">Tu panorama actual</h3>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-3">
              <MetricCard
                icon={<Users size={16} className="text-violet-500" />}
                value={countsLoading ? '—' : String(processCounts?.active ?? 0)}
                label={<ConceptLink onClick={() => setOpenPanel('proceso')}>Procesos activos</ConceptLink>}
                subLabel="Con sesiones en últimos 30 días"
              />
              <MetricCard
                icon={<UserX size={16} className="text-red-400" />}
                value={countsLoading ? '—' : String(processCounts?.inactive ?? 0)}
                label={<ConceptLink onClick={() => setOpenPanel('proceso')}>Procesos inactivos</ConceptLink>}
                subLabel="Sin sesiones en los últimos 30 días"
              />
              <MetricCard
                icon={<Users size={16} className="text-teal-500" />}
                value={countsLoading ? '—' : String(processCounts?.leads ?? 0)}
                label={<ConceptLink onClick={() => setOpenPanel('proceso')}>Próximos a iniciar</ConceptLink>}
                subLabel="Con primera sesión agendada"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                icon={<Archive size={16} className="text-teal-500" />}
                value={countsLoading ? '—' : String(processCounts?.historical ?? 0)}
                label="Procesos históricos"
                subLabel="Total desde el inicio"
                tooltipText="Suma de procesos activos, inactivos, en pausa y con derivación exitosa — el mismo estado (process_patient.status) que ves en la tab de Pacientes."
              />
              <MetricCard
                icon={<TrendingUp size={16} className="text-violet-500" />}
                value={loading ? '—' : String(metrics?.process_limit ?? 0)}
                label={<ConceptLink onClick={() => setOpenPanel('horarios')}>Límite interno según etapa</ConceptLink>}
                subLabel="Máximo de procesos asignables"
                tooltipText="El límite de procesos que el sistema puede asignarte en este momento, según tu etapa de crecimiento."
              />
            </div>
          </div>
        </div>

        {/* ── B. Texto introductorio ────────────────────────────────────── */}
        <div className="bg-[#E9E0FC] rounded-xl p-6 flex flex-col gap-4 border-l-4 border-[#665090]">
          <h3 className="text-base font-semibold text-[#2D2049]">
            ¿Cómo funciona la asignación de procesos en los primeros meses?
          </h3>
          <p className="text-sm text-[#333333] leading-[1.8]">
            Todas las psicólogas que se incorporan a Psi Mammoliti arrancan con un límite de{' '}
            <strong>5 <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink></strong>.
          </p>
          <p className="text-sm text-[#333333] leading-[1.8]">
            A medida que avanzás en tu camino de crecimiento, si tus{' '}
            <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink> se
            sostienen en el tiempo, ese límite sube: primero a 10, después a 15, y finalmente
            a 20 — momento en que dejas de estar en el período inicial, y pasás a contar con un esquema de asignaciones diferente.
          </p>
          <p className="text-sm text-[#333333] leading-[1.8]">
            El límite no es arbitrario. Se basa en tu{' '}
            <ConceptLink onClick={() => setOpenPanel('adherencia')}>adherencia temprana</ConceptLink>:
            cuántos de los{' '}
            <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink> que
            recibís logran sostenerse con vos en las primeras semanas.
          </p>
          <p className="text-sm text-[#333333] leading-[1.8]">
            Es la señal más clara que tenemos de que el vínculo terapéutico está funcionando.
          </p>
        </div>

        {/* ── C. Stepper vertical ──────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-[#4A3D73]">
              ¿Cuál es el camino de crecimiento?
            </h3>
            <InfoTooltip
              text="Podés saber en qué etapa estás mirando tu límite interno: 5 procesos = Etapa 1, 10 = Etapa 2, 15 = Etapa 3."
              placement="top"
            />
          </div>
          <p className="text-sm text-[#616161] leading-[1.8]">
            Hay tres etapas. En cada una recibís un grupo de{' '}
            <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink> y
            evaluamos tu{' '}
            <ConceptLink onClick={() => setOpenPanel('adherencia')}>adherencia temprana</ConceptLink>{' '}
            para decidir si avanzás a la siguiente.
          </p>

          <div className="flex flex-col mt-1">
            {ETAPAS.map((etapa, idx) => {
              const completada = isCompletada(etapa.numero);
              const isExpanded = expandedEtapa === etapa.numero;
              const isLast = idx === ETAPAS.length - 1;

              const headerBg = completada ? 'bg-[#ECFFD5]' : 'bg-[#E9E0FC]';
              const headerText = completada ? 'text-[#2D2049]' : 'text-[#616161]';
              const circleBg = completada ? 'bg-[#ECFFD5]' : 'bg-[#E9E0FC]';
              const circleText = completada ? 'text-[#2D2049]' : 'text-[#616161]';

              return (
                <div key={etapa.numero} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${circleBg} ${circleText}`}>
                      {completada ? <Check size={14} /> : etapa.numero}
                    </div>
                    {!isLast && <div className="w-0.5 flex-1 my-1.5 bg-[#E9E0FC] min-h-[20px]" />}
                  </div>

                  <div className="flex-1 mb-3 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleEtapa(etapa.numero)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${headerBg} ${headerText}`}
                    >
                      <span className="font-semibold text-sm">
                        Etapa {etapa.numero} — {etapa.titulo}
                        {etapa.numero === 3 && (
                          <span className="ml-1.5 font-normal opacity-70 text-xs">(condicional)</span>
                        )}
                      </span>
                      {isExpanded
                        ? <ChevronUp size={16} className="flex-shrink-0 opacity-70" />
                        : <ChevronDown size={16} className="flex-shrink-0 opacity-70" />}
                    </button>

                    {isExpanded && (
                      <div className="bg-white border border-[#E9E0FC] border-t-0 rounded-b-xl px-4 py-5 flex flex-col gap-4">
                        <p className="text-sm text-[#333333] leading-[1.8]">{etapa.descripcion}</p>
                        <div className="bg-[#F3EFFF] rounded-lg px-4 py-4">
                          <p className="text-xs font-semibold text-[#4A3D73] mb-2">Resultado esperado</p>
                          <p className="text-sm text-[#333333] leading-[1.8]">{etapa.resultadoEsperado}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Nodo destino: Fin período inicial */}
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#E6F381]">
                  <span className="text-base">🌟</span>
                </div>
              </div>
              <div className="flex-1 rounded-xl px-4 py-3" style={{ backgroundColor: '#2D2049' }}>
                <p className="text-sm font-semibold" style={{ color: '#FFFFFF' }}>Dejás de estar en el período inicial!</p>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: '#FFFFFF' }}>
                  Con 20{' '}
                  <ConceptLink onClick={() => setOpenPanel('proceso')} white>procesos</ConceptLink>{' '}
                  dejás de estar en el período inicial y pasás a contar con un esquema de asignaciones diferente.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── D. Callout advertencia ────────────────────────────────────── */}
        <div className={`rounded-xl p-6 flex gap-3 ${enPausa ? 'bg-[#FFDEDE] border-2 border-[#E86C6C]' : 'bg-[#FFDEDE]'}`}>
          <AlertTriangle size={18} className="text-[#2D2049] flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-3">
            {enPausa && (
              <p className="text-sm font-bold text-[#2D2049]">Tus asignaciones están pausadas</p>
            )}
            <p className="text-sm font-semibold text-[#2D2049]">
              ¿Qué pasa si no se alcanzan los objetivos?
            </p>
            <p className="text-sm text-[#333333] leading-[1.8]">
              Si al cierre de una etapa la{' '}
              <ConceptLink onClick={() => setOpenPanel('adherencia')}>adherencia temprana</ConceptLink>{' '}
              está por debajo de lo esperado,{' '}
              <strong>las nuevas asignaciones se pausan por al menos 6 meses</strong>.
            </p>
            <p className="text-sm text-[#333333] leading-[1.8]">
              Durante ese tiempo seguís atendiendo a tus{' '}
              <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos actuales</ConceptLink>{' '}
              con normalidad — la pausa aplica solo a la llegada de nuevos. Pasados los 6 meses,
              el equipo evalúa la situación y te comunica los pasos a seguir.
            </p>
          </div>
        </div>

        {/* ── E. Accordion "Para cuando llegues al equipo fijo" ────────── */}
        <div className="rounded-xl bg-[#F3EFFF] overflow-hidden">
          <button
            type="button"
            onClick={() => setEquipoFijoExpanded((prev) => !prev)}
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <span className="text-sm font-semibold text-[#4A3D73]">
              Para cuando termines tu período inicial de adaptación
            </span>
            {equipoFijoExpanded
              ? <ChevronUp size={16} className="text-[#8475B3]" />
              : <ChevronDown size={16} className="text-[#8475B3]" />}
          </button>

          {equipoFijoExpanded && (
            <div className="px-5 pb-6 flex flex-col gap-5">
              <p className="text-sm text-[#616161] leading-[1.8]">
                Cuando termines tu período inicial de adaptación, vas a encontrarte con un
                sistema de asignaciones diferente, ya que nuestro techo es que puedas tener,
                como máximo, 40 pacientes en simultáneo. Estos conceptos te van a resultar
                familiares porque ya los conocés en su versión más simple.
              </p>
              <div className="flex flex-col gap-2">
                {[
                  {
                    key: 'limite-pedido',
                    title: 'Límite pedido por vos',
                    content: (
                      <>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          Vas a poder <strong>declarar hasta cuántos pacientes querés atender</strong> en un momento dado. Ese número es tuyo — <strong>lo ajustás cuando querés</strong> y el sistema nunca lo va a superar.
                        </p>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          Es distinto al límite que el sistema calcula internamente: uno <strong>lo ponés vos</strong>, el otro lo define el sistema automáticamente según tu performance.
                        </p>
                      </>
                    ),
                  },
                  {
                    key: 'limite-interno',
                    title: 'Límite interno',
                    content: (
                      <>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          El sistema <strong>calcula automáticamente</strong> cuántos{' '}
                          <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink>{' '}
                          puede asignarte en base a tu historial. No es un número fijo: <strong>puede subir o bajar</strong> según tu performance.
                        </p>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          Durante tu período inicial de adaptación, ese límite se mueve según tu{' '}
                          <ConceptLink onClick={() => setOpenPanel('adherencia')}>adherencia temprana</ConceptLink>{' '}
                          — cuántos de tus{' '}
                          <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink>{' '}
                          sostuvieron sus primeras sesiones. Es el criterio que venís trabajando en el
                          camino de crecimiento.
                        </p>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          Una vez que terminás ese período, el criterio cambia: el sistema pasa a
                          mirar tu <strong>tasa de inactividad mensual</strong> (cuántos{' '}
                          <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink>{' '}
                          activos pasaron a inactivo cada mes). La lógica de fondo sigue siendo la
                          misma: sostener{' '}
                          <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink>{' '}
                          en el tiempo es lo que te habilita a recibir más.
                        </p>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          El límite interno <strong>se recalcula una vez por mes</strong>. Al terminar tu período
                          inicial, queda estipulado en 20{' '}
                          <ConceptLink onClick={() => setOpenPanel('proceso')}>procesos</ConceptLink>{' '}
                          — desde ahí puede crecer o disminuir.
                        </p>
                      </>
                    ),
                  },
                  {
                    key: 'reactivaciones',
                    title: 'Reactivaciones',
                    content: (
                      <>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          Un{' '}
                          <ConceptLink onClick={() => setOpenPanel('proceso')}>proceso</ConceptLink>{' '}
                          que pasó a inactivo no es definitivo. Si ese paciente decide retomar la
                          terapia con vos, su proceso vuelve a activo automáticamente.
                        </p>
                        <p className="text-sm text-[#333333] leading-[1.8]">
                          Lo importante: <strong>una reactivación siempre tiene cupo</strong>. El
                          sistema no la bloquea aunque estés cerca de tu límite — es una relación
                          terapéutica que ya existía, y eso tiene prioridad.
                        </p>
                      </>
                    ),
                  },
                ].map(({ key, title, content }) => (
                  <div key={key} className="bg-white rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleEquipoSection(key)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left"
                    >
                      <span className="text-sm font-semibold text-[#4A3D73]">{title}</span>
                      {expandedEquipoSection === key
                        ? <ChevronUp size={16} className="text-[#8475B3] shrink-0" />
                        : <ChevronDown size={16} className="text-[#8475B3] shrink-0" />}
                    </button>
                    {expandedEquipoSection === key && (
                      <div className="px-5 pb-5 flex flex-col gap-3">{content}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
